/**
 * 项目详情的低频管理操作统一放在「编辑项目信息」最底部，减少页头按钮拥挤。
 * 复用二维码、门户接口与原删除权限；子弹窗使用 Radix，避免被编辑弹窗的焦点锁和遮罩拦住。
 * 查看二维码或门户不会关闭编辑表单，尚未保存的字段因此可以继续编辑。
 */
import React, { useState } from "react";
import { Link2, Loader2, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { toast } from "./ui/sonner";
import { OwnerLinkCheck } from "./OwnerLinkCheck";
import { ProjectQrButton } from "./wms/ProjectQrButton";

/** project 使用已保存的项目资料；onDeleted 由详情页决定回列表还是关闭项目抽屉。 */
export default function ProjectManagementActions({ api, project, zh, user, onDeleted }) {
  const [ownerLink, setOwnerLink] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // 仅在接口成功后打开门户弹窗，失败时保留编辑上下文；修复链接也复用同一生成入口。
  const genOwnerLink = async () => {
    setLinkBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/hub/owner-link`);
      setOwnerLink(data.url?.startsWith("http") ? data.url : `${window.location.origin}/owner/${data.token}`);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    finally { setLinkBusy(false); }
  };

  // 移动入口不改变删除规则：仍须确认项目地址，进入回收站保留 15 天后再彻底删除。
  const deleteProject = async () => {
    if (!window.confirm(zh ? `确定删除项目「${project.address}」？\n\n项目将进入回收站保存 15 天，期间可在「系统管理 → 回收站」恢复，15 天后自动彻底删除。` : `Delete project "${project.address}"?\n\nIt will be moved to the recycle bin for 15 days (restorable), then permanently deleted.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success(zh ? "已移入回收站（保留 15 天）" : "Moved to recycle bin (15 days)");
      onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "删除失败" : "Delete failed")); }
    finally { setDeleting(false); }
  };

  return <>
    <ProjectQrButton api={api} project={project} zh={zh} />
    <Dialog open={!!ownerLink} onOpenChange={(open) => { if (!open) setOwnerLink(null); }}>
      <DialogTrigger asChild>
        <Button type="button" data-testid="hub-owner-link-btn" size="sm" variant="outline" disabled={linkBusy} onClick={genOwnerLink} className="h-9 text-xs">
          {linkBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden="true" /> : <Link2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" />}{zh ? "业主门户" : "Owner link"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="hub-owner-link-modal" closeLabel={zh ? "关闭" : "Close"}>
        <DialogHeader>
          <DialogTitle>{zh ? "业主门户链接" : "Owner portal link"}</DialogTitle>
          <DialogDescription>{zh ? "只读页面：工程进度、现场照片、业主可见讨论。可发给业主，无需登录。" : "Read-only page for the owner, no login needed."}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <input readOnly value={ownerLink || ""} aria-label={zh ? "业主门户链接" : "Owner portal link"} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 text-xs rounded-lg border border-slate-200 px-2 py-1.5 bg-slate-50" data-testid="hub-owner-link-url" />
          <Button type="button" size="sm" className="h-9 text-xs" data-testid="hub-owner-link-copy"
            onClick={async () => { try { await navigator.clipboard.writeText(ownerLink); toast.success(zh ? "已复制" : "Copied"); } catch { toast.error(zh ? "复制失败，请手动复制" : "Copy failed"); } }}>{zh ? "复制" : "Copy"}</Button>
        </div>
        <OwnerLinkCheck api={api} projectId={project.id} zh={zh} onFixed={genOwnerLink} />
        <div className="flex justify-end gap-2">
          <Button type="button" size="sm" variant="outline" className="h-9 text-xs" onClick={() => window.open(ownerLink, "_blank")} data-testid="hub-owner-link-open">{zh ? "预览" : "Preview"}</Button>
          <DialogClose asChild><Button type="button" size="sm" className="h-9 text-xs">{zh ? "关闭" : "Close"}</Button></DialogClose>
        </div>
      </DialogContent>
    </Dialog>
    {["admin", "eng_manager", "ceo"].includes(user?.role) && <Button type="button" data-testid="hub-delete-project-btn" size="sm" variant="outline" disabled={deleting} onClick={deleteProject}
      className="h-9 text-xs text-red-600 border-red-200 hover:bg-red-600 hover:text-white sm:ml-auto">
      {deleting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden="true" /> : <Trash2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" />}{zh ? "删除项目" : "Delete"}
    </Button>}
  </>;
}
